import { useEffect } from "react";
import { NavLink } from "react-router-dom";
import { X, LogOut, ChevronRight, ShieldCheck } from "lucide-react";

/**
 * Mobile-only side-drawer navigation. Shared by PartnerShell + AdminShell.
 *
 * Why a separate component (not just removing `hidden lg:flex` from the
 * existing aside)?
 *   1. The desktop sidebar is sticky `top-0 h-screen` — that breaks on
 *      mobile because it would compete with the page scroll.
 *   2. Mobile needs a backdrop + close-on-tap-outside + body-scroll lock,
 *      none of which apply to desktop.
 *   3. Keeping them separate keeps the desktop CSS unchanged so we don't
 *      accidentally regress a layout that already works.
 *
 * Props:
 *   open      — drawer visibility flag (controlled by parent)
 *   onClose   — called when backdrop/close-X is tapped, or after a nav
 *               click so the drawer doesn't stay open over the new page
 *   navItems  — array of { to, icon, label, end? }
 *   variant   — "partner" | "admin" — tints the active-link state to
 *               match the desktop shell (emerald for partner, blue for
 *               admin) so the colour grammar stays consistent
 *   user      — current user (for the signed-in footer block)
 *   onLogout  — sign-out handler
 *   testIdPrefix — "partner" | "admin" so the test ids match the
 *                  desktop shell's naming convention
 *   partnerBusinessName — only used in partner variant header
 */
export default function MobileNav({
    open,
    onClose,
    navItems,
    variant = "partner",
    user,
    onLogout,
    testIdPrefix = "partner",
    partnerBusinessName,
}) {
    // Body-scroll lock while drawer is open — without this, the page
    // behind the drawer scrolls through the backdrop on touchscreens.
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    // Close on Escape — small affordance for desktop users who happen to
    // open the drawer on a narrow window.
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const activeCls = variant === "admin"
        ? "bg-blue-50 text-blue-700 font-medium"
        : "bg-emerald-50 text-emerald-700 font-medium";
    const idleCls = "text-slate-600 hover:bg-slate-50";

    return (
        <>
            {/* Backdrop. `lg:hidden` ensures the drawer machinery is a
                no-op on desktop where the persistent sidebar handles
                navigation. */}
            <div
                data-testid={`${testIdPrefix}-mobile-backdrop`}
                onClick={onClose}
                className={`lg:hidden fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity ${
                    open ? "opacity-100" : "opacity-0 pointer-events-none"
                }`}
            />
            {/* Drawer panel — slides in from the left. Width 16rem
                matches the desktop `w-60` so users coming from desktop
                feel at home. */}
            <aside
                data-testid={`${testIdPrefix}-mobile-drawer`}
                className={`lg:hidden fixed top-0 left-0 bottom-0 w-64 bg-white border-r border-slate-200 z-50 flex flex-col transition-transform duration-200 ${
                    open ? "translate-x-0" : "-translate-x-full"
                }`}
            >
                <div className="px-5 py-5 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm shrink-0 ${
                            variant === "admin"
                                ? "bg-gradient-to-br from-blue-600 to-emerald-500"
                                : "bg-gradient-to-br from-emerald-500 to-blue-600"
                        }`}>P</div>
                        <div className="min-w-0">
                            <div className="font-bold text-sm leading-tight">PayGateway</div>
                            <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5 flex items-center gap-1 truncate">
                                {variant === "admin"
                                    ? (<><ShieldCheck className="w-2.5 h-2.5 shrink-0" /> Admin</>)
                                    : <span className="truncate">{partnerBusinessName || "Partner"}</span>
                                }
                            </div>
                        </div>
                    </div>
                    <button
                        data-testid={`${testIdPrefix}-mobile-close`}
                        onClick={onClose}
                        className="p-1.5 -mr-1.5 rounded-md text-slate-500 hover:bg-slate-100"
                        aria-label="Close navigation"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
                    {navItems.map((it) => (
                        <NavLink
                            data-testid={`nav-${testIdPrefix}-mobile-${it.label.toLowerCase().replace(/\s/g, "")}`}
                            key={it.to}
                            to={it.to}
                            end={it.end}
                            onClick={onClose}
                            className={({ isActive }) =>
                                `flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm transition-colors ${
                                    isActive ? activeCls : idleCls
                                }`
                            }
                        >
                            <it.icon className="w-4 h-4 shrink-0" />
                            <span className="truncate">{it.label}</span>
                            <ChevronRight className="w-3.5 h-3.5 ml-auto text-slate-300" />
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-slate-100">
                    <div className="px-3 py-2 mb-1">
                        <div className="text-xs text-slate-400">Signed in</div>
                        <div className="text-sm font-medium truncate">{user?.email}</div>
                    </div>
                    <button
                        data-testid={`${testIdPrefix}-mobile-logout`}
                        onClick={() => { onClose(); onLogout(); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-50"
                    >
                        <LogOut className="w-4 h-4" /> Sign out
                    </button>
                </div>
            </aside>
        </>
    );
}
